import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';

import { ToastService } from '../toast/toast.service';
import { environment } from '../../../environments/environment';

/**
 * Marca los pings de salud en segundo plano para que error.interceptor.ts no los pase por el
 * reporter de observabilidad: con el backend caído, cada reintento de 15s generaría un reporte
 * nuevo sin aportar nada (la caída ya se reportó con el request original que falló).
 */
export const BACKGROUND_HEALTH_CHECK = new HttpContextToken<boolean>(() => false);

// Cada cuánto se reintenta el ping mientras el backend está caído. Con el backend arriba no hay
// polling de ninguna clase: el estado se actualiza pasivamente con los requests reales de la app.
// Un ping colgado (timeout compartido de 30s) puede estirar el ciclo efectivo hasta ~30s: el
// guard de "ping en vuelo" no dispara uno nuevo hasta que el anterior termine — aceptado.
const PING_INTERVAL_MS = 15_000;

/**
 * Estado global de disponibilidad del backend. Lo alimenta error.interceptor.ts (markDown en
 * fallos "el backend nunca contestó", markUp en cualquier respuesta que pruebe que está vivo)
 * y lo consume el banner global de src/app/core/backend-status/backend-down-banner.ts.
 */
@Injectable({ providedIn: 'root' })
export class BackendStatusService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);

  private readonly downSignal = signal(false);
  private readonly checkingSignal = signal(false);
  private timer: ReturnType<typeof setInterval> | null = null;

  /** true mientras el backend está caído (ningún request logra una respuesta suya). */
  readonly down = this.downSignal.asReadonly();
  /** true mientras hay un ping en vuelo (deshabilita el "Probar ahora" del banner). */
  readonly checking = this.checkingSignal.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopTimer());
  }

  markDown(): void {
    if (this.downSignal()) {
      return;
    }
    this.downSignal.set(true);
    this.startTimer();
  }

  markUp(): void {
    this.stopTimer();
    if (!this.downSignal()) {
      return;
    }
    this.downSignal.set(false);
    // Solo en la transición caído → arriba: los markUp repetidos (cada respuesta exitosa
    // pasa por acá vía el interceptor) no deben spamear el toast.
    this.toast.success('Se restableció la conexión con el servidor.');
  }

  /** Ping inmediato, sin esperar el ciclo de 15s (botón "Probar ahora" del banner). */
  checkNow(): void {
    this.ping();
  }

  private ping(): void {
    // Sin caída no hay nada que probar; y un ping en vuelo no se duplica (el ciclo de 15s
    // y el botón del banner pueden coincidir).
    if (!this.downSignal() || this.checkingSignal()) {
      return;
    }
    this.checkingSignal.set(true);
    // /ping es público (permitAll) y contesta texto plano: responseType 'text' evita que un
    // body no-JSON haga fallar un ping que en realidad fue exitoso.
    this.http
      .get(`${environment.apiUrl}/ping`, {
        responseType: 'text',
        context: new HttpContext().set(BACKGROUND_HEALTH_CHECK, true),
      })
      .subscribe({
        next: () => {
          this.checkingSignal.set(false);
          // Redundante a propósito: el tap del error.interceptor ya marcó up cuando la respuesta
          // del propio ping atravesó la cadena. Se conserva para que el servicio no dependa de
          // ese orden — markUp() es idempotente y el toast solo sale en la transición.
          this.markUp();
        },
        error: () => {
          // Sigue caído: el ciclo de 15s va a reintentar solo.
          this.checkingSignal.set(false);
        },
      });
  }

  private startTimer(): void {
    if (this.timer !== null) {
      return;
    }
    this.timer = setInterval(() => this.ping(), PING_INTERVAL_MS);
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
